// src/components/admin/AdminLogin.js  — UPDATED: shows role badge after login
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  LockClosedIcon, UserIcon, EyeIcon, EyeSlashIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading,  setLoading]  = useState(false);
  const [showPw,   setShowPw]   = useState(false);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ok = await login(formData.username, formData.password);
      if (ok) {
        toast.success('Login successful!');
        navigate('/admin/dashboard');
      } else {
        toast.error('Invalid username or password');
      }
    } catch {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; }
    @keyframes spin { to { transform:rotate(360deg); } }
    @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }

    .al-wrap {
      min-height:100vh; display:flex;
      background:linear-gradient(150deg,#f0fdf4 0%,#ffffff 50%,#f0f9ff 100%);
      position:relative; overflow:hidden;
    }
    .al-blob { position:absolute; border-radius:50%; filter:blur(70px); pointer-events:none; }
    .al-blob-1 { width:22rem; height:22rem; background:#a7f3d0; opacity:0.18; top:-4rem;    left:-4rem; }
    .al-blob-2 { width:18rem; height:18rem; background:#99f6e4; opacity:0.14; bottom:-3rem; right:-3rem; }
    .al-blob-3 { width:14rem; height:14rem; background:#bae6fd; opacity:0.12; top:40%;     right:10%; }

    .al-center {
      flex:1; display:flex; align-items:center; justify-content:center;
      padding:2rem 1rem; position:relative; z-index:2;
    }
    .al-card {
      background:white; border-radius:2rem;
      box-shadow:0 24px 60px -10px rgba(0,0,0,0.13);
      border:1px solid #f3f4f6;
      width:100%; max-width:440px; overflow:hidden;
      animation:fadeUp 0.45s ease both;
    }

    /* Header */
    .al-card-header {
      background:linear-gradient(135deg,#059669,#0891b2);
      padding:2rem 2rem 1.75rem; text-align:center;
    }
    .al-logo-ring {
      width:80px; height:80px; border-radius:1.5rem;
      background:rgba(255,255,255,0.2);
      display:flex; align-items:center; justify-content:center;
      margin:0 auto 1rem;
      border:2px solid rgba(255,255,255,0.4);
      overflow:hidden; padding:6px;
    }
    .al-card-header h1 { font-size:1.5rem; font-weight:900; color:white; margin-bottom:0.25rem; }
    .al-card-header p  { color:rgba(255,255,255,0.85); font-size:0.88rem; font-weight:600; }
    .al-badge {
      display:inline-flex; align-items:center; gap:0.35rem;
      background:rgba(255,255,255,0.22);
      color:white; font-size:0.7rem; font-weight:700; letter-spacing:0.5px;
      padding:0.28rem 0.85rem; border-radius:9999px; margin-top:0.75rem;
      border:1px solid rgba(255,255,255,0.3);
    }
    .al-badge svg { width:0.75rem; height:0.75rem; }

    /* Body */
    .al-body { padding:2rem; }
    .al-field { margin-bottom:1.25rem; }
    .al-label { display:block; font-size:0.82rem; font-weight:700; color:#374151; margin-bottom:0.5rem; }
    .al-input-wrap { position:relative; }
    .al-input-icon { position:absolute; left:0.9rem; top:50%; transform:translateY(-50%);
                     width:1rem; height:1rem; color:#9ca3af; pointer-events:none; }
    .al-input {
      width:100%; padding:0.7rem 1rem 0.7rem 2.5rem;
      border:1.5px solid #e5e7eb; border-radius:0.875rem;
      font-size:0.9rem; font-family:'Nunito',sans-serif; font-weight:600;
      color:#111827; background:#f9fafb; outline:none; transition:all 0.2s;
    }
    .al-input:focus { border-color:#059669; background:white; box-shadow:0 0 0 3px rgba(5,150,105,0.1); }
    .al-pw-toggle { position:absolute; right:0.9rem; top:50%; transform:translateY(-50%);
                    width:1rem; height:1rem; color:#9ca3af; cursor:pointer; background:none; border:none; }
    .al-pw-toggle:hover { color:#059669; }

    .al-submit {
      width:100%; padding:0.8rem;
      background:linear-gradient(135deg,#10b981,#059669);
      color:white; font-weight:800; font-size:0.95rem;
      border:none; border-radius:9999px; cursor:pointer;
      transition:all 0.3s; font-family:'Nunito',sans-serif;
      box-shadow:0 4px 14px rgba(16,185,129,0.3);
      display:flex; align-items:center; justify-content:center; gap:0.5rem;
      margin-top:1.5rem;
    }
    .al-submit:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 20px rgba(16,185,129,0.35); }
    .al-submit:disabled { opacity:0.6; cursor:not-allowed; transform:none; }

    /* Role info boxes */
    .al-roles { display:grid; grid-template-columns:1fr 1fr; gap:0.75rem; margin-top:1.35rem; }
    .al-role-box { border-radius:0.875rem; padding:0.75rem 0.85rem; border:1.5px solid; }
    .al-role-title { font-size:0.75rem; font-weight:800; margin-bottom:0.35rem; display:flex; align-items:center; gap:0.3rem; }
    .al-role-title svg { width:0.75rem; height:0.75rem; }
    .al-role-item { font-size:0.68rem; font-weight:600; color:#64748b; line-height:1.6; }
    .al-role-item::before { content:'✓ '; color:#059669; font-weight:800; }
    .al-role-item.denied::before { content:'✗ '; color:#dc2626; }

    .al-divider { text-align:center; margin:1.25rem 0 0; font-size:0.78rem; color:#9ca3af; }
    .al-divider a { color:#059669; font-weight:700; text-decoration:none; }
    .al-divider a:hover { text-decoration:underline; }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="al-wrap">
        <div className="al-blob al-blob-1" />
        <div className="al-blob al-blob-2" />
        <div className="al-blob al-blob-3" />

        <div className="al-center">
          <div className="al-card">

            <div className="al-card-header">
              <div className="al-logo-ring">
                <img
                  src="/images/logo.png" alt="Fresh Hills"
                  style={{ width:'48px', height:'48px', objectFit:'contain' }}
                  onError={e => { e.target.onerror=null; e.target.style.display='none'; e.target.parentNode.innerHTML='🥛'; }}
                />
              </div>
              <h1>Fresh Hills</h1>
              <p>QR Fridge Management System</p>
              <span className="al-badge">
                <ShieldCheckIcon /> ADMIN &amp; STAFF PORTAL
              </span>
            </div>

            <div className="al-body">
              <form onSubmit={handleSubmit}>
                <div className="al-field">
                  <label className="al-label">Username</label>
                  <div className="al-input-wrap">
                    <UserIcon className="al-input-icon" />
                    <input
                      type="text" name="username" autoComplete="username"
                      value={formData.username} onChange={handleChange}
                      placeholder="Enter your username"
                      className="al-input" required
                    />
                  </div>
                </div>

                <div className="al-field">
                  <label className="al-label">Password</label>
                  <div className="al-input-wrap">
                    <LockClosedIcon className="al-input-icon" />
                    <input
                      type={showPw ? 'text' : 'password'} name="password"
                      autoComplete="current-password"
                      value={formData.password} onChange={handleChange}
                      placeholder="Enter your password"
                      className="al-input" style={{ paddingRight:'2.5rem' }} required
                    />
                    <button type="button" className="al-pw-toggle"
                      onClick={() => setShowPw(!showPw)}>
                      {showPw
                        ? <EyeSlashIcon style={{ width:'1rem', height:'1rem' }} />
                        : <EyeIcon      style={{ width:'1rem', height:'1rem' }} />}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} className="al-submit">
                  {loading ? (
                    <>
                      <span style={{ width:'1rem', height:'1rem', border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'white', borderRadius:'50%', display:'inline-block', animation:'spin 0.7s linear infinite' }} />
                      Logging in…
                    </>
                  ) : (
                    <><LockClosedIcon style={{ width:'1rem', height:'1rem' }} />Login</>
                  )}
                </button>
              </form>

              {/* Role info */}
              <div className="al-roles">
                <div className="al-role-box" style={{ borderColor:'#a7f3d0', background:'#f0fdf4' }}>
                  <div className="al-role-title" style={{ color:'#059669' }}>
                    <ShieldCheckIcon /> Admin
                  </div>
                  <div className="al-role-item">Full access</div>
                  <div className="al-role-item">Manage staff</div>
                  <div className="al-role-item">Products & Fridges</div>
                  <div className="al-role-item">Sales Report</div>
                </div>
                <div className="al-role-box" style={{ borderColor:'#bfdbfe', background:'#eff6ff' }}>
                  <div className="al-role-title" style={{ color:'#2563eb' }}>
                    <UserIcon /> Staff
                  </div>
                  <div className="al-role-item">View Orders</div>
                  <div className="al-role-item">Stock Monitor</div>
                  <div className="al-role-item">Refill Schedule</div>
                  <div className="al-role-item denied" style={{ opacity:0.7 }}>No edit access</div>
                </div>
              </div>

              <p className="al-divider">
                <a href="/">← Back to Fresh Hills Store</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;